/**
 * Permission levels with their corresponding weights
 * Higher weight = more permissions
 */
export const PERMISSION_LEVELS = {
  NONE: { level: 'None', weight: 0 },
  VIEWER: { level: 'Viewer', weight: 10 },
  EDITOR: { level: 'Editor', weight: 20 },
  MANAGER: { level: 'Manager', weight: 30 },
} as const;

export type PermissionLevelName = 'None' | 'Viewer' | 'Editor' | 'Manager';

export interface PermissionLevel {
  level: PermissionLevelName;
  weight: number;
}

/**
 * Available permission modules in the system
 */
export type PermissionModule =
  | 'notification_mgmt'
  | 'affiliate_mgmt'
  | 'creative_mr_oven'
  | 'adverity_import'
  | 'media_acct_mgmt'
  | 'brand_mgmt'
  | 'user_mgmt'
  | 'taxonomy'
  | 'drawer_mgmt'
  | 'lift_ai'
  | 'grapevine';

/**
 * Permission object for a single module
 */
export interface ModulePermission {
  level: PermissionLevelName;
  weight: number;
}

/**
 * All permissions for a user
 */
export type UserPermissions = {
  [key in PermissionModule]?: ModulePermission;
};

/**
 * JWT Claims structure with permissions
 */
export interface JWTClaims {
  name: string;
  picture: string;
  is_superadmin: boolean;
  permissions: UserPermissions;
  email: string;
  email_verified: boolean;
  auth_time: number;
  user_id: string;
  firebase: {
    identities: {
      email: string[];
    };
    sign_in_provider: string;
  };
  iat: number;
  exp: number;
  aud: string;
  iss: string;
  sub: string;
}

/**
 * Permission check result
 */
export interface PermissionCheckResult {
  hasPermission: boolean;
  userLevel: PermissionLevelName;
  requiredLevel: PermissionLevelName;
  isSuperAdmin: boolean;
}

/**
 * Action types that can be performed
 */
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'manage';

/**
 * Mapping of actions to minimum required permission levels
 */
export const ACTION_PERMISSION_MAP: Record<PermissionAction, PermissionLevelName> = {
  view: 'Viewer',
  create: 'Editor',
  edit: 'Editor',
  delete: 'Manager',
  manage: 'Manager',
};

